import {FullEmailThreadContent} from "./EmailContentDto";
import {DecryptedGateKeeperAccount} from "./DecryptedGateKeeperAccount";
import {EmailAnalysisDto} from '../services/messages/responses/EmailAnalysisDto';
import {EmailSourceDto} from './EmailSourceDto';
import {ContactDto} from "../services/messages/ContactDto";
import {CalendarEntryData} from "../services/calendar/CalendarEntryData";
import {PgpKey, UnknownPgpKey} from "./PgpKey";
import {EmailRuleDto} from "../services/rules/dto/EmailRuleDto";
import {FileDto} from "../services/protected/dto/FileDto";
import DbDraftEmail, {SendEmailDto} from "./DbDraftEmail";
import {UserDto} from "./UserDto";
import {EnrichedDavContact} from "../services/messages/VCardContactData";

export interface DummyItemContent {
    dummyType: "loading" | "none";
}

export interface LoginRequiredContent {
    loginRequired: true;
}

export type ItemContent =
    FullEmailThreadContent
    | DecryptedGateKeeperAccount
    | DummyItemContent
    | EmailAnalysisDto
    | EmailSourceDto
    | ContactDto
    | CalendarEntryData
    | PgpKey
    | UnknownPgpKey
    | EmailRuleDto
    | FileDto
    | LoginRequiredContent
    | UserDto
    | EnrichedDavContact;

export function hasItemContent(content: ItemContent | undefined): boolean {
    return !!content && (!isDummyContent(content) || content?.dummyType === "loading");
}

export function isDummyContent(content: ItemContent | undefined): content is DummyItemContent {
    if (!content) {
        return false;
    }
    return (content as DummyItemContent).dummyType !== undefined;
}

export function isEmailContent(content: any): content is FullEmailThreadContent {
    if (!content) {
        return false;
    }
    const asEmail = content as FullEmailThreadContent;
    return asEmail.Emails !== undefined
        && asEmail.Id !== undefined
        && asEmail.Emails[0]
        && asEmail.Emails[0].Content !== undefined
        && asEmail.Emails[0].FolderId !== undefined;
}

export function isSendEmailDto(content: any): content is SendEmailDto {
    if (!content) {
        return false;
    }
    const asEmail = content as SendEmailDto;
    return asEmail.VerbatimHeaders !== undefined
        && asEmail.EditUid !== undefined;
}

export function isDbDraftEmail(content: any): content is DbDraftEmail {
    if (!content) {
        return false;
    }
    const asEmail = content as DbDraftEmail;
    return asEmail.IsDraft !== undefined
        && asEmail.EditUid !== undefined
        && asEmail.BodyText !== undefined;
}

export function isEmailAnalysisContent(content: ItemContent | undefined): content is EmailAnalysisDto {
    if (!content) {
        return false;
    }
    return (content as EmailAnalysisDto).SpamFilterResults !== undefined;
}

export function isRawEmailContent(content: ItemContent | undefined): content is EmailSourceDto {
    if (!content) {
        return false;
    }
    const emailSource = content as EmailSourceDto;
    return emailSource.Decoded !== undefined && emailSource.Parse;
}

export function isEmailSourceContent(content: ItemContent | undefined): content is EmailSourceDto {
    if (!content) {
        return false;
    }
    return (content as EmailSourceDto).Decoded !== undefined;
}

export function isAccountContent(content: ItemContent | undefined): content is DecryptedGateKeeperAccount {
    if (!content) {
        return false;
    }
    return (content as DecryptedGateKeeperAccount).fields !== undefined;
}

export function isContactContent(content: ItemContent | undefined): content is EnrichedDavContact {
    if (!content) return false;

    return (content as EnrichedDavContact).props !== undefined;
}

export function isCalendarEntryContent(content: ItemContent | undefined): content is CalendarEntryData {
    if (!content) return false;

    return (content as CalendarEntryData).calendar !== undefined;
}

export function isPgpKeyContent(content: any): content is PgpKey {
    if (!content) return false;

    return content && (content as PgpKey).keyHexId !== undefined;
}

export function isUnknownPgpKeyContent(content: ItemContent | undefined): content is UnknownPgpKey {
    if (!content) return false;

    return content && (content as UnknownPgpKey).unknownKeyHexId !== undefined;
}

export function isEmailRuleContent(content: ItemContent | undefined): content is EmailRuleDto {
    if (!content) return false;

    return content && (content as EmailRuleDto).Conditions !== undefined;
}

export function isLoginRequiredContent(content: ItemContent | undefined): content is LoginRequiredContent {
    if (!content) return false;

    return content && (content as LoginRequiredContent).loginRequired;
}

export function isUserDtoContent(content: ItemContent | undefined): content is UserDto {
    if (!content) return false;

    return content && (content as UserDto).WebUsername !== undefined;
}
